import type { ControllerRenderProps, FieldPath, FieldValues } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { FormFieldWrapper, type FormFieldWrapperProps } from '../components/FormFieldWrapper';
import { Cascader } from './Cascader';
import type { CascaderOption, ShowCheckedStrategy } from './helpers';
import { getDisplayText } from './utils/display-utils';

/**
 * CascaderField プロパティ型
 */
export type CascaderFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<FormFieldWrapperProps<TFieldValues, TName>, 'children'> & {
  /** カスケードオプション */
  options: CascaderOption[];
  /** プレースホルダー */
  placeholder?: string;
  /** 検索を有効にする（単一選択のみ） */
  showSearch?: boolean;
  /** 変更をトリガーするタイミング */
  changeOnSelect?: boolean;
  /** カスタム表示レンダラー（単一選択のみ） */
  displayRender?: (labels: string[]) => string;
  /** 複数選択を有効にする */
  multiple?: boolean;
  /** チェック済みアイテムの表示戦略（複数選択時のみ） */
  showCheckedStrategy?: ShowCheckedStrategy;
  /** 表示するタグの最大数（複数選択時のみ） */
  maxTagCount?: number | 'responsive';
  /** 値変更時のコールバック */
  onChange?: (value: string[] | string[][]) => void;
  /** 無効化 */
  disabled?: boolean;
  /** 無効化するアイテムの値配列 */
  disabledItemValues?: string[];
  /** 無効化アイテムの理由マップ（値 → 理由テキスト） */
  disabledItemReasons?: Record<string, string>;
  /** クリアボタンを表示するか（値が入力されている場合のみ表示） */
  showAllClear?: boolean;
  /** 末端ノード（リーフ）のみ選択可能にする（中間階層での選択を禁止） */
  onlyLeafSelectable?: boolean;
};

/**
 * CascaderField コンポーネント
 *
 * FieldItem でラップされた Cascader コンポーネント
 * React Hook Form と統合され、ラベル・バリデーション・エラーメッセージを含む
 *
 * 使用例:
 * ```tsx
 * <CascaderField
 *   control={form.control}
 *   name="location"
 *   label="地域"
 *   placeholder="地域を選択してください"
 *   options={[
 *     {
 *       label: '浙江省',
 *       value: 'zhejiang',
 *       children: [
 *         {
 *           label: '杭州市',
 *           value: 'hangzhou',
 *           children: [
 *             { label: '西湖区', value: 'xihu' },
 *           ],
 *         },
 *       ],
 *     },
 *   ]}
 *   required
 * />
 * ```
 */
export const CascaderField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  required,
  supportText,
  showSupportText,
  readOnly,
  displayValue,
  className,
  options,
  placeholder = '選択してください',
  showSearch = false,
  changeOnSelect = false,
  displayRender,
  multiple = false,
  showCheckedStrategy,
  maxTagCount,
  size = 'sm',
  onChange,
  disabled,
  disabledItemValues,
  disabledItemReasons,
  showAllClear = false,
  onlyLeafSelectable = true,
}: CascaderFieldProps<TFieldValues, TName>) => {
  // readOnly 時に選択パスからラベルを解決して表示値を生成
  const fieldValue = useWatch({ control, name });
  const readOnlyDisplayValue = (() => {
    if (!readOnly || displayValue) return displayValue;
    if (!Array.isArray(fieldValue) || fieldValue.length === 0) return undefined;
    return getDisplayText(fieldValue as string[], options, displayRender);
  })();

  return (
    <FormFieldWrapper
      control={control}
      name={name}
      label={label}
      required={required}
      supportText={supportText}
      showSupportText={showSupportText}
      readOnly={readOnly}
      displayValue={readOnlyDisplayValue}
      className={className}
      size={size}
    >
      {({
        field,
        fieldId,
        hasError,
      }: {
        field: ControllerRenderProps<TFieldValues, TName>;
        fieldId: string;
        hasError: boolean;
      }) => {
        // field.value を適切な型に変換
        const cascaderValue = multiple
          ? Array.isArray(field.value)
            ? field.value
            : []
          : Array.isArray(field.value) && !Array.isArray(field.value[0])
            ? field.value
            : [];

        return (
          <Cascader
            id={fieldId}
            showAllClear={showAllClear}
            options={options}
            value={cascaderValue}
            onChange={(value) => {
              field.onChange(value);
              onChange?.(value);
            }}
            placeholder={placeholder}
            disabled={disabled || false}
            hasError={hasError}
            showSearch={showSearch}
            changeOnSelect={changeOnSelect}
            multiple={multiple}
            size={size}
            disabledItemValues={disabledItemValues || []}
            disabledItemReasons={disabledItemReasons}
            onlyLeafSelectable={onlyLeafSelectable}
            {...(displayRender && { displayRender })}
            {...(showCheckedStrategy && { showCheckedStrategy })}
            {...(maxTagCount !== undefined && { maxTagCount })}
          />
        );
      }}
    </FormFieldWrapper>
  );
};

CascaderField.displayName = 'CascaderField';
